<template>
  <BlocksEntityBlockGridView
    v-if="data.gridView"
    :mapped-data="mapedData"
    :data="data"
    :max-items-to-show="maxItemsToShow"
    :space-between="spaceBetween"
    :page-data="pageData"
    class="container2 my-4 lg:my-6.5"
  />
  <section v-else>
    <div class="py-10.5 lg:py-19.5">
      <div
        class="mb-5 flex items-center justify-between max-lg:container lg:container2"
        :class="{ 'mb-8 lg:mb-11': data.productView }"
      >
        <h2
          v-if="data?.title"
          class="ui-h3 relative w-full text-primary"
          :style="{ textAlign: data?.title_position || 'left' } as StyleValue"
        >
          <span
            class="block whitespace-pre-wrap capitalize"
            v-html="data.title"
          />
        </h2>
        <div
          v-if="!hideArrows && mapedData?.length && swiperInited"
          class="ml-auto shrink-0 max-lg:hidden"
        >
          <button
            ref="prevEl"
            :aria-label="$t('previousSlide')"
            class="mr-5 inline-flex h-[46px] w-[46px] items-center justify-center rounded-full border border-solid border-black transition-all disabled:opacity-50"
          >
            <UiIcon name="arrow_left" class="h-6 w-6 text-primary" />
          </button>
          <button
            ref="nextEl"
            :aria-label="$t('nextSlide')"
            class="inline-flex h-[46px] w-[46px] items-center justify-center rounded-full border border-solid border-black transition-all disabled:opacity-50"
          >
            <UiIcon name="arrow_right" class="h-6 w-6 text-primary" />
          </button>
        </div>
      </div>
      <div
        v-if="data.tabsView && data.productView && tabsData"
        class="container flex"
        :class="[
          data?.title_position === 'center'
            ? 'justify-center'
            : 'justify-start',
        ]"
      >
        <ul class="mb-10 mr-auto inline-flex gap-x-5 overflow-x-auto">
          <li
            v-for="(tab, idx) in tabsData"
            :key="tab.label"
            class="text-gray-disabled flex-shrink-0 whitespace-nowrap text-sm capitalize"
            :class="[
              activeTab === idx ? 'cursor-default underline' : 'cursor-pointer',
            ]"
            @click="activeTab = idx"
          >
            {{ tab.label }}
          </li>
        </ul>
      </div>

      <UiScrollableSlider
        :items-list="mapedData"
        :slides-count="data.slidesCount?.mobile || 1.5"
        :gap-x="spaceBetween.default"
        class="container px-0 lg:!hidden"
      >
        <template
          #card="{
            item,
            idx,
          }: {
            item:
              | (EntityData & Pick<BlockEntity, 'active' | 'link'>)
              | CatalogData;
            idx: number;
          }"
        >
          <SharedEntityItemCard
            v-if="!data.productView"
            :data="data"
            :item="item as EntityData & Pick<BlockEntity, 'active' | 'link'>"
          />
          <CommonProductCardPlp
            v-else
            :product="item as CatalogData"
            :product-index="idx"
            :plp-type="plpTypes.default"
            :statistic-item-list-name="pageData?.currentTranslation?.title"
            :image-brightness-filter="data.image_brightness_filter"
            :image-background-color="data.imageBackgroundColorMobile"
            :card-content-position="data?.card_content_position"
          />
        </template>
      </UiScrollableSlider>

      <div v-if="mapedData.length" class="container2 max-lg:hidden">
        <Swiper
          class="w-full"
          :slides-per-view="data.slidesCount?.mobile || 1.5"
          :space-between="16"
          :loop="false"
          :centered-slides="false"
          :a11y="true"
          :initial-slide="0"
          :modules="modules"
          :navigation="navigation"
          :breakpoints="{
            [screens.lg]: {
              slidesPerView: data?.slidesCount?.desktop || 3,
              centeredSlides: false,
              initialSlide: 0,
              spaceBetween: spaceBetween.lg,
            },
          }"
          @swiper="initSwiper"
          @resize="onUpdateSwiper"
        >
          <SwiperSlide
            v-for="(item, idx) in mapedData.slice(0, maxItemsToShow)"
            :key="item.id"
          >
            <SharedEntityItemCard
              v-if="!data.productView"
              :data="data"
              :item="item as EntityData & Pick<BlockEntity, 'active' | 'link'>"
            />
            <CommonProductCardPlp
              v-else
              :product="item as CatalogData"
              :product-index="idx"
              :plp-type="plpTypes.default"
              :statistic-item-list-name="pageData?.currentTranslation?.title"
              :image-brightness-filter="data.image_brightness_filter"
              :image-background-color="data.imageBackgroundColor"
              :card-content-position="data?.card_content_position"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation, Swiper as SwiperInstance } from "swiper";
import { NavigationOptions } from "swiper/types";
import { debounce } from "lodash-es";
import { StyleValue } from "vue";
import { hash } from "ohash";
import {
  BlockEntitiesContent,
  BlockEntity,
  BlockEntitiesContentTypesE,
  EntityData,
  EntitiesResponse,
  CatalogData,
  PageData,
} from "ecom-types";
import { screens } from "~/configs/breakpointsConfig.json";
import { plpTypes } from "~/configs";

const { $api } = useNuxtApp();

const { t } = useI18n();

const { viewItemListEvent } = useEvents();
const isMounted = useMounted();

const props = defineProps<{
  data: BlockEntitiesContent;
  blockName: string;
  pageData?: PageData | null;
}>();
const hideArrows = ref(false);
const activeTab = ref(0);
const entityResponse = ref<EntitiesResponse | null>(null);

const marginButtonTop = ref(0);
const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);
const swiperInited = ref(false);

const maxItemsToShow = ref(15);
const maxItemsToShowForTabs = ref(999);
// const isCentered = ref(false);

const navigation = reactive({
  prevEl,
  nextEl,
  disabledClass: "opacity-20 cursor-not-allowed hover:bg-transparent",
}) as NavigationOptions;

const modules = [Navigation];

const ids = props.data.entities?.reduce(
  (acc, el) => {
    if (el.active && el.id) {
      acc.push(el.id);
    }
    return acc;
  },
  [] as Array<string | number>,
);

const catalogQueryString = computed(() => {
  const entities = props.data?.entities ?? [];
  const temp = entities.reduce<number[]>((acc, current) => {
    if (current.active) {
      acc.push(current.id as number);
    }
    return acc;
  }, []);

  const params: any = {};
  if (props.data.type === BlockEntitiesContentTypesE.collections) {
    params.column = "collection_sort_order";
    params.order_by = "asc";
  }
  return objectToQuery({
    [String(props.data.type)]: temp,
    count: props.data.tabsView
      ? maxItemsToShowForTabs.value
      : maxItemsToShow.value,
    ...params,
  });
});

const { data: resData } = props.data.productView
  ? await useAsyncCachedData(
      hash([
        `product-view-${props.data.productView}`,
        unref(catalogQueryString),
      ]),
      () => {
        return $api.products.getCatalog(catalogQueryString);
      },
    )
  : await useAsyncCachedData(`${props?.data?.type}-${ids?.toString()}`, () => {
      return $api.entities.getEntities(
        props?.data?.type || "",
        objectToQuery({
          ids,
          count: props.data.tabsView
            ? maxItemsToShowForTabs.value
            : maxItemsToShow.value,
        }),
      );
    });

if (props.data.tabsView && props.data.productView) {
  const { data: entityRes } = await useAsyncCachedData(
    `tabsView-${props?.data?.type}-{${ids?.toString()}`,
    () => {
      return $api.entities.getEntities(
        props?.data?.type || "",
        objectToQuery({
          ids,
        }),
      );
    },
  );
  entityResponse.value = entityRes.value;
}

const tabsData = computed(() => {
  if (
    props.data.tabsView &&
    props.data.productView &&
    entityResponse.value &&
    resData.value
  ) {
    return entityResponse.value?.data?.reduce(
      (acc, el: EntityData) => {
        const tab = {
          id: el.id,
          label: el?.currentTranslation?.title || "",
          items:
            groupProducts((resData.value?.data as CatalogData[]) || [], el) ||
            [],
        };
        acc.push(tab);
        return acc;
      },
      [
        {
          id: 0,
          label: t("all"),
          items: (resData.value?.data as CatalogData[]) || [],
        },
      ],
    );
  }
  return null;
});

const mapedData = computed<
  CatalogData[] | (EntityData[] & Pick<BlockEntity, "active" | "link">) | []
>(() => {
  let temp:
    | CatalogData[]
    | (EntityData[] & Pick<BlockEntity, "active" | "link">)
    | [] = [];
  if (!props.data.productView) {
    temp =
      (resData.value?.data?.reduce(
        (acc, el: any) => {
          const tempEl = props.data.entities?.find((item) => item.id === el.id);
          if (tempEl && tempEl.active) {
            acc.push({
              ...el,
              ...tempEl,
            });
          }
          return acc;
        },
        [] as Array<BlockEntity & Omit<EntityData, "id" | "sort_order">>,
      ) as EntityData[] & Omit<EntityData, "id" | "sort_order">) || [];
    temp.sort((a: any, b: any) => a.sort_order - b.sort_order);
  } else if (props.data.productView && props.data.tabsView) {
    temp = (tabsData.value?.[activeTab.value]?.items as CatalogData[]) || [];
  } else {
    temp = (resData.value?.data as CatalogData[]) || [];
  }

  return temp;
});

const spaceBetween = computed(() => {
  if (props.data.productView) {
    return {
      lg: 16,
      default: 16,
    };
  }
  return {
    lg: 16,
    default: 16,
  };
});

watchEffect(() => {
  if (isMounted.value && props.data?.productView && mapedData.value?.length) {
    viewItemListEvent(mapedData.value, {
      item_list_name: props.pageData?.currentTranslation?.title,
    });
  }
});

function initSwiper(params: typeof SwiperInstance) {
  marginButtonTop.value =
    Number(params.slides[0]?.querySelector("picture > img")?.height || 100) /
      2 -
    24;

  debounceSwiper(params);
}

const debounceSwiper = debounce((params) => {
  hideArrows.value = params?.params?.slidesPerView >= mapedData.value.length;
  // isCentered.value = params?.params.centeredSlides;
  swiperInited.value = true;
}, 200);

function onUpdateSwiper(params: any) {
  debounceSwiper(params);
}

// const itemsNumber = computed(() => mapedData.value?.length ?? 0);

function groupProducts(items: CatalogData[] | [], tab: EntityData) {
  if (!items?.length) {
    return [];
  }
  if (props.data.type === BlockEntitiesContentTypesE.materials) {
    return items.filter((el) => el.materials?.some((i) => i.id === tab.id));
  } else if (props.data.type === BlockEntitiesContentTypesE.collections) {
    return items.filter((el) => el.collections_ids?.includes(tab.id));
  } else if (props.data.type === BlockEntitiesContentTypesE.categories) {
    return items.filter((el) => el.categories_ids?.includes(tab.id));
  }
}
</script>

<style scoped>
.is-materials.is-centered .swiper-slide:not(.swiper-slide-active) {
  transform: scale(0.75);
  opacity: 0.5;
}

/* .diamond-slider-title {
  color: #0f0f0f;
  font-size: 30px;
  font-weight: 400;
  letter-spacing: 0.6px;
  line-height: normal;
  max-width: none;
  margin: auto;
} */
.diamond-slider {
  padding: 40px 0;
}
.diamond-slider picture {
  transition: scale 0.25s;
}
.diamond-slider picture:hover {
  scale: 1.16;
}
.diamond-slider h3 {
  font-size: 16px;
  text-align: center;
}
.diamond-slider a:hover h3 {
  @apply font-medium;
  @apply text-primary;
}
</style>
